/**
 * Money is stored as signed integer "micros" (1e-6 of the major unit) in the
 * currency it was earned in. That keeps Stripe's minor units and Adsterra's
 * sub-cent revenue exact, and stays inside JS safe integers up to ~9 billion.
 */

export const MICROS = 1_000_000;

// Currencies Stripe treats as zero- or three-decimal.
// https://docs.stripe.com/currencies#zero-decimal
const ZERO_DECIMAL = new Set([
  'bif',
  'clp',
  'djf',
  'gnf',
  'jpy',
  'kmf',
  'krw',
  'mga',
  'pyg',
  'rwf',
  'ugx',
  'vnd',
  'vuv',
  'xaf',
  'xof',
  'xpf',
]);
const THREE_DECIMAL = new Set(['bhd', 'jod', 'kwd', 'omr', 'tnd']);

export function stripeExponent(currency: string): number {
  const c = currency.toLowerCase();
  if (ZERO_DECIMAL.has(c)) return 0;
  if (THREE_DECIMAL.has(c)) return 3;
  return 2;
}

/** Stripe minor-unit integer → micros. */
export function stripeAmountToMicros(amount: number, currency: string): number {
  return Math.round(amount * 10 ** (6 - stripeExponent(currency)));
}

// ISO 4217 minor units, for platforms that send amounts in minor units of any currency.
// https://www.six-group.com/en/products-services/financial-information/data-standards.html
const ISO_ZERO_DECIMAL = new Set([
  'bif',
  'clp',
  'djf',
  'gnf',
  'isk',
  'jpy',
  'kmf',
  'krw',
  'pyg',
  'rwf',
  'ugx',
  'uyi',
  'vnd',
  'vuv',
  'xaf',
  'xof',
  'xpf',
]);
const ISO_THREE_DECIMAL = new Set(['bhd', 'iqd', 'jod', 'kwd', 'lyd', 'omr', 'tnd']);

/** Decimal places of a currency's minor unit under ISO 4217 (JPY 0, USD 2, KWD 3). Stripe keeps its own table. */
export function currencyExponent(currency: string): number {
  const c = currency.toLowerCase();
  if (ISO_ZERO_DECIMAL.has(c)) return 0;
  if (ISO_THREE_DECIMAL.has(c)) return 3;
  return 2;
}

export function toMicros(major: number): number {
  return Math.round(major * MICROS);
}

export function fromMicros(micros: number): number {
  return micros / MICROS;
}

/** Display currencies with reliable daily FX reference rates (ECB set). */
export const DISPLAY_CURRENCIES = [
  'USD',
  'EUR',
  'GBP',
  'CHF',
  'CAD',
  'AUD',
  'NZD',
  'JPY',
  'CNY',
  'HKD',
  'SGD',
  'SEK',
  'NOK',
  'DKK',
  'PLN',
  'CZK',
  'HUF',
  'RON',
  'TRY',
  'ILS',
  'INR',
  'IDR',
  'KRW',
  'MXN',
  'BRL',
  'ZAR',
  'THB',
  'PHP',
  'MYR',
  'ISK',
] as const;

const decimalsCache = new Map<string, number>();

/** Fraction digits a currency is normally displayed with (USD → 2, JPY → 0). */
export function displayDecimals(currency: string): number {
  let d = decimalsCache.get(currency);
  if (d === undefined) {
    try {
      d =
        new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions()
          .maximumFractionDigits ?? 2;
    } catch {
      d = 2;
    }
    decimalsCache.set(currency, d);
  }
  return d;
}

const formatCache = new Map<string, Intl.NumberFormat>();

export interface FormatMoneyOptions {
  decimals?: number;
  compact?: boolean;
  signed?: boolean;
  locale?: string;
}

const COMPACT = [
  [1e12, 'T'],
  [1e9, 'B'],
  [1e6, 'M'],
  [1e3, 'K'],
] as const;
let compactSupported: boolean | undefined;

/** Whether this engine formats `notation: 'compact'` (some phone JS engines ignore it). */
function compactNotation(): boolean {
  if (compactSupported === undefined) {
    try {
      compactSupported =
        new Intl.NumberFormat('en', { notation: 'compact' }).resolvedOptions().notation === 'compact';
    } catch {
      compactSupported = false;
    }
  }
  return compactSupported;
}

/** Compact money without `notation: 'compact'`: "$12.3" with the suffix after its last digit, "$12.3K". */
export function formatCompactFallback(
  value: number,
  currency: string,
  opts: FormatMoneyOptions = {},
): string {
  const [scale, suffix] = COMPACT.find(([s]) => Math.abs(value) >= s * 0.9995) ?? [1, ''];
  const plain = { ...opts, compact: false };
  if (!suffix) return formatMoney(value, currency, { ...plain, decimals: 0 });
  const raw = value / scale;
  const scaled = Math.abs(raw) >= 99.95 ? Math.round(raw) : Math.round(raw * 10) / 10;
  const decimals = Number.isInteger(scaled) ? 0 : 1;
  return formatMoney(scaled, currency, { ...plain, decimals }).replace(/(\d)(?!.*\d)/, `$1${suffix}`);
}

export function formatMoney(value: number, currency: string, opts: FormatMoneyOptions = {}): string {
  if (opts.compact && !compactNotation()) return formatCompactFallback(value, currency, opts);
  const decimals = opts.decimals ?? displayDecimals(currency);
  const key = `${opts.locale ?? ''}|${currency}|${decimals}|${opts.compact ? 1 : 0}|${opts.signed ? 1 : 0}`;
  let f = formatCache.get(key);
  if (!f) {
    f = new Intl.NumberFormat(opts.locale, {
      style: 'currency',
      currency,
      notation: opts.compact ? 'compact' : 'standard',
      minimumFractionDigits: opts.compact ? 0 : decimals,
      maximumFractionDigits: opts.compact ? 1 : decimals,
      signDisplay: opts.signed ? 'exceptZero' : 'auto',
    });
    formatCache.set(key, f);
  }
  return f.format(value);
}

/** A formatter made once and kept (making one is dear: a dashboard formats hundreds of numbers). */
function kept(key: string, make: () => Intl.NumberFormat): Intl.NumberFormat {
  let f = formatCache.get(key);
  if (!f) {
    f = make();
    formatCache.set(key, f);
  }
  return f;
}

export function formatPercent(ratio: number, opts: { signed?: boolean; decimals?: number } = {}): string {
  const decimals = opts.decimals ?? (Math.abs(ratio) < 0.1 ? 1 : 0);
  const signDisplay = opts.signed ? 'exceptZero' : 'auto';
  return kept(
    `%|${decimals}|${signDisplay}`,
    () =>
      new Intl.NumberFormat(undefined, {
        style: 'percent',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        signDisplay,
      }),
  ).format(ratio);
}

export function formatCount(value: number, compact = false): string {
  return kept(
    `#|${compact ? 1 : 0}`,
    () =>
      new Intl.NumberFormat(undefined, {
        notation: compact ? 'compact' : 'standard',
        maximumFractionDigits: compact ? 1 : 0,
      }),
  ).format(value);
}
